import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Worker } from "../../types/worker.type";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import useGetRequest from "../../hooks/useGetRequest";
import { SalaryType } from "../../types/salary.type";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Title } = Typography;
const { Option } = Select;

export default function AssignedWorkers() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { itemId, type, name } = useParams();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWorkerSalary, setSelectedWorkerSalary] = useState<SalaryType | null>(null);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { data: workerList, loading: listLoading } = useGetRequest(`/worker/list`);
  const { data: producesList } = useGetRequest(`/machine/${itemId}/produces`);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/${type}/${itemId}/workers`);
      setWorkers(response.data.workers);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получения сотрудников");
    } finally {
      setLoading(false);
    }
  };

  const handleAddWorker = () => {
    setIsModalOpen(true);
  };

  const handleEditWorker = async (worker: Worker) => {
    setSelectedWorker(worker);
    setSelectedWorkerSalary(worker.salaryType);
    setIsEditModalOpen(true);

    try {
      if (worker.salaryType === SalaryType.PERMONTH) {
        const monthlyPricing = worker.pricings.find((p: any) => p.perMonth);
        editForm.setFieldsValue({
          perMonth: monthlyPricing?.perMonth,
        });
      } else if (worker.salaryType === SalaryType.PERUNIT) {
        const pricingValues: any = {};
        worker.pricings.forEach((pricing: any) => {
          pricingValues[pricing.for] = pricing.perUnit;
        });
        editForm.setFieldsValue(pricingValues);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
      message.error("Ошибка получения прайсингов");
    }
  };

  const handleSubmitForm = async (values: any) => {
    let pricingData = [];
    let dataToSend = {};
    if (selectedWorkerSalary === SalaryType.PERMONTH) {
      dataToSend = {
        workerId: values.workerId,
        perMonth: values.perMonth,
      };
    } else if (selectedWorkerSalary === SalaryType.PERUNIT) {
      pricingData = producesList?.produces.map((produce: any) => ({
        for: produce.produceId,
        perUnit: values[produce.produceId],
      }));

      dataToSend = {
        workerId: values.workerId,
        pricings: pricingData,
      };
    }

    setConfirmLoading(true);
    try {
      await api.post(`/${type}/${itemId}/worker`, dataToSend);
      message.success("Сотрудник успешно добавлен.");
      fetchWorkers();
      setIsModalOpen(false);
      setConfirmLoading(false);
      form.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error adding worker", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditSubmit = async (values: any) => {
    let pricingData = [];
    let dataToSend = {};

    if (selectedWorkerSalary === SalaryType.PERMONTH) {
      dataToSend = {
        perMonth: values.perMonth,
      };
    } else if (selectedWorkerSalary === SalaryType.PERUNIT) {
      pricingData = producesList?.produces.map((produce: any) => ({
        for: produce.produceId,
        perUnit: values[produce.produceId],
      }));

      dataToSend = {
        pricings: pricingData,
      };
    }

    setConfirmLoading(true);
    try {
      await api.put(`/${type}/${itemId}/worker/${selectedWorker?._id}/pricing`, dataToSend);
      message.success("Прайсинги успешно обновлены.");
      fetchWorkers();
      setIsEditModalOpen(false);
      setConfirmLoading(false);
      editForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error updating pricing", error);
      message.error(error.response.data.error);
    }
  };

  const handleDeleteWorker = async (workerId: string) => {
    try {
      await api.delete(`/${type}/${itemId}/${workerId}/worker`);
      message.success("Сотрудник успешно удален.");
      fetchWorkers();
    } catch (error: any) {
      console.log("Error deleting worker", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фамилия",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Прозвище",
      dataIndex: "nickname",
      key: "nickname",
    },
    {
      title: "Имя пользователя",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюты зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Цены",
      key: "pricings",
      render: (_: any, record: Worker) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "1400px",
            }}
          >
            {record.pricings.map((pricing, index) => (
              <p key={index}>
                {index + 1}.{pricing.forName}, Ставка:{pricing.perUnit}
                {pricing.perMonth}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, worker: Worker) => {
        const menu = (
          <Menu>
            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Accountant) && (
                <>
                  <Menu.Item onClick={() => handleEditWorker(worker)}>
                    Изменить
                  </Menu.Item>
                  <Popconfirm
                    title="Вы уверены, что хотите удалить этого сотрудника?"
                    onConfirm={() => handleDeleteWorker(worker._id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Menu.Item>Удалить</Menu.Item>
                  </Popconfirm>
                </>
              )}
            <Menu.Item
              onClick={() => {
                const name = `${worker.name} ${worker.surname}`;
                navigate(`/${worker._id}/${name}/salary/salary-reports`);
              }}
            >
              Смотреть отчеты
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={2}>Сотрудники {name}</Title>
        {(authState.user?.role === UserRole.Admin ||
          authState.user?.role === UserRole.Accountant) && (
            <Button type="primary" onClick={handleAddWorker}>
              Добавить
            </Button>
          )}
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={workers}
        rowKey={(record) => record._id}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить сотрудника"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="workerId"
            label="Выберете сотрудника"
            rules={[{ required: true, message: "Выберите сотрудника" }]}
          >
            <Select
              placeholder="Выберите сотрудника"
              loading={listLoading}
              onChange={(value) => {
                const selectedWorker = workerList?.find(
                  (worker: Worker) => worker._id === value
                );
                if (selectedWorker) {
                  setSelectedWorkerSalary(selectedWorker.salaryType);
                }
              }}
            >
              {workerList?.map((worker: Worker) => (
                <Option key={worker._id} value={worker._id}>
                  {worker.name} {worker.surname} - Зарплата: {worker.salaryType}{" "}
                  {worker.salaryCurrency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {selectedWorkerSalary === SalaryType.PERMONTH ? (
            <Form.Item
              name="perMonth"
              label="Ставка в месяц"
              rules={[{ required: true, message: "Введите ставку" }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          ) : selectedWorkerSalary === SalaryType.PERUNIT ? (
            <>
              {producesList &&
                producesList.produces.map((produce: any) => (
                  <div key={produce.produceId}>
                    <Title level={5}>Ставка для {produce.produceName}</Title>
                    <Form.Item
                      name={produce.produceId}
                      label="Ставка за единицу"
                      rules={[{ required: true, message: "Введите ставку" }]}
                    >
                      <InputNumber min={0} />
                    </Form.Item>
                  </div>
                ))}
            </>
          ) : (
            <></>
          )}
        </Form>
      </Modal>

      <Modal
        title="Изменить"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={() => editForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical">
          {selectedWorkerSalary === SalaryType.PERMONTH ? (
            <Form.Item
              name="perMonth"
              label="Ставка в месяц"
              rules={[{ required: true, message: "Введите ставку" }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          ) : selectedWorkerSalary === SalaryType.PERUNIT ? (
            <>
              {producesList &&
                producesList.produces.map((produce: any) => {
                  return (
                    <div key={produce.produceId}>
                      <Title level={5}>Ставка для {produce.produceName}</Title>
                      <Form.Item
                        name={produce.produceId}
                        label="Ставка за единицу"
                        rules={[{ required: true, message: "Введите ставку" }]}
                      >
                        <InputNumber min={0} />
                      </Form.Item>
                    </div>
                  );
                })}
            </>
          ) : (
            <></>
          )}
        </Form>
      </Modal>
    </div>
  );
} 